const FEEDBACK_MS = 1800;

/** Older/insecure-context browsers have no navigator.clipboard - fall back to execCommand. */
async function writeToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to the legacy path (permission denied, non-secure origin, …).
    }
  }

  const helper = document.createElement("textarea");
  helper.value = text;
  helper.setAttribute("readonly", "");
  helper.style.position = "fixed";
  helper.style.opacity = "0";
  helper.style.pointerEvents = "none";
  document.body.append(helper);
  helper.select();

  let ok: boolean;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }
  helper.remove();
  return ok;
}

function buildButton(value: string, label: string): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "copy-button";
  button.dataset.state = "idle";
  button.setAttribute("aria-label", `${label} kopieren`);
  button.title = `${label} kopieren`;

  const icon = document.createElement("span");
  icon.className = "copy-button-icon";
  icon.setAttribute("aria-hidden", "true");
  icon.textContent = "⧉";

  // Announced to screen readers on change, but visually conveyed by the icon swap.
  const status = document.createElement("span");
  status.className = "sr-only";
  status.setAttribute("role", "status");

  button.append(icon, status);

  let resetHandle: ReturnType<typeof setTimeout> | undefined;

  button.addEventListener("click", async () => {
    const ok = await writeToClipboard(value);

    button.dataset.state = ok ? "copied" : "failed";
    icon.textContent = ok ? "✓" : "✕";
    status.textContent = ok ? `${label} kopiert` : `${label} konnte nicht kopiert werden`;

    clearTimeout(resetHandle);
    resetHandle = setTimeout(() => {
      button.dataset.state = "idle";
      icon.textContent = "⧉";
      status.textContent = "";
    }, FEEDBACK_MS);
  });

  return button;
}

/**
 * Turns any `[data-copy]` element into a "copy this value" affordance by appending a
 * small button next to it. The value comes from `data-copy`, the human-readable name
 * for announcements from `data-copy-label` (falling back to the element's own text).
 */
export function initCopyButtons(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>("[data-copy]").forEach((el) => {
    const value = el.dataset.copy;
    if (!value || el.dataset.copyReady === "true") return;

    const label = el.dataset.copyLabel || el.textContent?.trim() || "Wert";
    el.dataset.copyReady = "true";
    el.append(buildButton(value, label));
  });
}
